import type { FC } from 'react';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form@latest';
import { ErrorMessage } from '@hookform/error-message';
import { useDispatch } from 'react-redux';
import cn from 'classnames';

import { _notNil } from '@/littledash';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';
import RemoveButton from '@/components/UI/RemoveButton';
import { errorToast, successToast } from '@/helpers';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { useRequest } from '@/support/Hooks/request';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';

interface AddTreatmentsProps {
  onAddTreatments: () => void;
}

interface TreatmentField {
  name: string;
}

interface AddTreatmentsFormData {
  treatments: Array<TreatmentField>;
}

const LIMIT_ENTRIES = 20;

const ERROR_MESSAGES = {
  DUPLICATE: 'Treatment names must be unique',
  REQUIRED: 'This field is required',
  VALIDATION_ERROR: 'Error validating treatment name',
  SUBMIT_ERROR: 'There was an error creating the treatment',
} as const;

const AddTreatments: FC<AddTreatmentsProps> = ({ onAddTreatments }) => {
  const dispatch = useDispatch();
  const [validating, setValidating] = useState(false);
  const [apiError, setApiError] = useState<Error | null>(null);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<AddTreatmentsFormData>({
    defaultValues: { treatments: [{ name: '' }] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'treatments',
  });

  const uniqueInSubmission = (value: string, index: number): string | true => {
    const { treatments } = getValues();
    const duplicates = treatments?.find(({ name }, i) => name === value && index !== i);
    if (_notNil(duplicates)) {
      return ERROR_MESSAGES.DUPLICATE;
    }
    return true;
  };

  const closeModal = () => dispatch({ type: 'CLOSE_MODAL' });

  const { sendRequest: addTreatmentTypes, requestSending: addingTreatmentTypes } = useRequest({
    route: 'teams.treatmentTypes.post',
    method: 'post',
  });

  const onSubmit = async (data: AddTreatmentsFormData) => {
    try {
      await addTreatmentTypes({ treatment_types: data.treatments });
      onAddTreatments();
      closeModal();
      successToast('Created new treatment');
    } catch (error) {
      setApiError(error as Error);
      errorToast(ERROR_MESSAGES.SUBMIT_ERROR);
    }
  };

  const validateUniqueTreatmentName = async (value: string): Promise<string | true> => {
    try {
      setValidating(true);
      const { data } = await Http.get<{ valid: boolean }>(
        apiRoute('teams.treatmentTypes.validate', {
          treatmentName: value,
        })
      );
      return data.valid === true ? true : ERROR_MESSAGES.DUPLICATE;
    } catch (error) {
      if (notAborted(error)) {
        setApiError(error as Error);
      }
      return ERROR_MESSAGES.VALIDATION_ERROR;
    } finally {
      setValidating(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('ui-card center mw6', { ui__disabled: addingTreatmentTypes })}
      data-test-component="AddTreatments"
      data-testid="add-treatments-form"
    >
      <div className="pa3">
        <h3 className="f4 lh-title normal" data-testid="add-treatments-title">
          Add treatments
        </h3>
        <p>Up to a maximum of {LIMIT_ENTRIES}</p>
      </div>
      {_notNil(apiError) && (
        <ApiErrorBanner
          title="There was an error with your request"
          text="An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support."
          error={apiError}
        />
      )}
      {fields.map((field, index) => (
        <div key={field.id} className={cn('pa3', { 'bt b--moon-gray': index > 0 })}>
          <label htmlFor={`treatments.${index}.name`}>Treatment name</label>
          <div className="flex justify-between">
            <input
              type="text"
              id={`treatments.${index}.name`}
              autoFocus={index === 0}
              className={cn('mr3 w-100 mb0', { input__error: _notNil(errors?.treatments?.[index]?.name) })}
              data-testid={`treatment-name-input-${index}`}
              {...register(`treatments.${index}.name`, {
                required: ERROR_MESSAGES.REQUIRED,
                validate: {
                  uniqueInSubmission: (value) => uniqueInSubmission(value, index),
                  uniqueFromAPI: validateUniqueTreatmentName,
                },
              })}
            />
            <RemoveButton
              onClick={() => remove(index)}
              isDisabled={fields.length === 1}
              testId={`remove-treatment-${index}`}
            />
          </div>
          <ErrorMessage
            errors={errors}
            name={`treatments.${index}.name`}
            render={({ message }) => (
              <p className="f6 red db pt2" data-testid={`treatment-error-${index}`}>
                {message}
              </p>
            )}
          />
        </div>
      ))}
      <div className="ph3">
        <Button
          paleBlue
          pill
          icon="add_new"
          disabled={fields.length >= LIMIT_ENTRIES}
          onClick={() => append({ name: '' })}
          testId="add-treatment-button"
        >
          Add new
        </Button>
      </div>
      <div className={cn('pa3 mt3 bt b--moon-gray', { ui__disabled: validating })}>
        <Button submit testId="save-treatments-button">
          Save
        </Button>
        <Button plain className="ml3" onClick={closeModal} testId="cancel-treatments-button">
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default AddTreatments;
